import { Component, OnInit, ChangeDetectionStrategy, Output, EventEmitter, Input, DestroyRef, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, combineLatest, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewerLayoutService } from '../../../services/viewer-layout/viewer-layout.service';
import { NgStyle, NgClass, AsyncPipe } from '@angular/common';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PanelResizerComponent, TooltipDirective } from '@tailormap-viewer/shared';

@Component({
    selector: 'tm-bottom-panel',
    templateUrl: './bottom-panel.component.html',
    styleUrls: ['./bottom-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PanelResizerComponent,
        NgStyle,
        MatToolbar,
        MatIconButton,
        TooltipDirective,
        MatIcon,
        NgClass,
        AsyncPipe,
    ],
    host: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '(window:keydown.escape)': 'onDocumentEscape($event)',
    },
})
export class BottomPanelComponent implements OnInit {
  private layoutService = inject(ViewerLayoutService);
  private destroyRef = inject(DestroyRef);


  public tooltips = {
    minimize: $localize `:@@core.shared.minimize-panel:Minimize panel`,
    maximize: $localize `:@@core.shared.maximize-panel:Maximize panel`,
    close: $localize `:@@core.shared.close-panel:Close panel`,
    restore: $localize `:@@core.shared.restore-panel-height:Restore panel height`,
  };

  public static readonly MINIMUM_PANEL_HEIGHT_PX = 115;

  @Input({ required: true })
  public isVisible$: Observable<boolean> = of(false);

  @Input({ required: true })
  public title$: Observable<string> = of('');

  @Input()
  public initialHeight = 350;

  @Input()
  public set maximized(maximized: boolean) {
    this.isMaximized = maximized;
  }

  @Input()
  public set minimized(minimized: boolean) {
    this.isMinimized = minimized;
  }

  @Input()
  public mobile = false;

  @Input()
  public set height(height: number | null) {
    if (height) {
      this.heightSubject.next(height);
    }
  }

  @Output()
  public heightChanged = new EventEmitter<number>();

  @Output()
  public closed = new EventEmitter();

  public onDocumentEscape(event: Event): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container');
    if (overlayContainer && overlayContainer.querySelector('.cdk-overlay-pane')) {
      return;
    }
    if (this.isMaximized) {
      this.isMaximized = false;
      return;
    }
    this.isVisible$.pipe(take(1)).subscribe(visible => {
      if (visible) {
        event.preventDefault();
        event.stopPropagation();
        this.closed.emit();
      }
    });
  }

  private heightSubject = new BehaviorSubject(350);

  public isMinimized = false;
  public isMaximized = false;
  public isUserResizing = false;

  public ngOnInit(): void {
    this.heightSubject.next(this.initialHeight || 350);
    combineLatest([
      this.isVisible$,
      this.heightSubject.asObservable(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([ visible, height ]) => {
        this.layoutService.setBottomPadding(visible ? height : 0);
      });
  }

  public sizeChanged(changedHeight: number) {
    let initialHeight = this.heightSubject.value;
    if (this.isMinimized) {
      initialHeight = 0;
    }
    if (this.isMaximized) {
      initialHeight = window.innerHeight;
    }
    this.isMinimized = false;
    this.isMaximized = false;
    const computedHeight = initialHeight - changedHeight;
    this.isUserResizing = true;
    const height = Math.max(computedHeight, BottomPanelComponent.MINIMUM_PANEL_HEIGHT_PX);
    this.heightSubject.next(height);
    this.heightChanged.emit(height);
    setTimeout(() => { this.isUserResizing = false; }, 0);
  }

  public getHeight() {
    if (this.isMaximized || this.isMinimized) {
      return '';
    }
    return `${this.heightSubject.value}px`;
  }

  public onMaximizeClick(): void {
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      this.isMinimized = false;
    }
  }

  public onMinimizeClick(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isMinimized) {
      this.isMaximized = false;
    }
  }

  public onCloseClick(): void {
    this.closed.emit();
  }

}
